import { useCachedFlagFallback } from '@/app-shell/flags/offline-cached-flag-decisions';
import { posthog, useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { SESSION_PAGE_V2_FLAG_KEY } from '@/app-shell/flags/session-page-v2-flag';
import {
  LABS_FEATURE,
  useIsAutoEnrolledSessionLayoutV2,
  useLabsFeatureOptIn,
} from '@/app-shell/settings/heidi-labs/early-access/labs-overrides';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
// Composed by `useIsRibbonSidebarEnabled` and by the two Session Page V2 gates,
// all three of which stay here — see the note above.
import {
  NEW_SESSION_LIST_FLAG_KEY,
  SESSION_LAYOUT_MANAGED_RELEASE_FLAG_KEY,
  useIsNewSessionListEnabled as useIsNewSessionListFlagEnabled,
  useIsSessionLayoutManagedReleaseEnabled as useIsSessionLayoutManagedReleaseLiveEnabled,
  useIsSessionPageV2GaChangeManagementEnabled,
} from '@heidi/session/flags';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';

import {
  IS_CHUNK_RECORDING_ENABLED,
  USE_RECORDING_V2,
} from '@/app-shell/flags/capture-flags';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';

/**
 * PostHog: gates the transcript-tab PDF download icon (start/end,
 * timestamps, and transcript text via POST /export-pdf) on the V1 and V2
 * live transcript gutters. Default OFF. The legacy plain-text pane is
 * out of scope — it has no chunk timestamps to format.
 * Cloud reads PostHog; on-prem `useFeatureFlagEnabled` falls through to
 * OpenFeature and stays off until the key is in the on-prem bundle.
 */
export const useIsTranscriptPdfDownloadEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-transcript-pdf-download') ?? false;
};

/**
 * The new session list, WITH its last online decision replayed while the flag
 * provider is unreachable.
 *
 * The package getter is the live read and stays the source of truth whenever
 * anyone can answer; this body only adds the offline replay, which is app-side
 * by necessity — the cache is a localStorage/keychain pair and
 * `@heidi/session/flags` is a headless door that may hold neither.
 *
 * Offline, the live read collapses to its `false` default, which took
 * the session list back to the legacy layout AND (because
 * `useIsSessionPageV2Enabled` ANDs this) dropped the session detail page to V1
 * mid-consult. Composed HERE rather than at the two V2 gates so the list, the
 * routing and the detail layout cannot disagree about which experience the user
 * is in.
 *
 * `useBuildSessionPath` inside `@heidi/session` still reads the live value, so
 * while offline it builds the LEGACY `/scribe/session/:id` path. That is a
 * redirect, not a broken route: `src/pages/scribe/session/[...id].tsx` reads
 * this getter and replaces itself with `/scribe/sessions/:id`. Moving the
 * replay behind the door would mean handing the package a localStorage port,
 * which is a bigger change than a release fix should carry.
 */
export const useIsNewSessionListEnabled = () => {
  const fromFlagsmith = useIsNewSessionListFlagEnabled();
  const fromOfflineCache = useCachedFlagFallback(NEW_SESSION_LIST_FLAG_KEY);
  // The package getter already ORs in the managed-release opt-in through the
  // seam `_app.tsx` configures at module scope.
  return fromFlagsmith || fromOfflineCache;
};

// Flagsmith targets the managed cohort; offline replay preserves its last decision.
export const useIsSessionLayoutManagedReleaseEnabled = () => {
  const fromFlagsmith = useIsSessionLayoutManagedReleaseLiveEnabled();
  const fromOfflineCache = useCachedFlagFallback(SESSION_LAYOUT_MANAGED_RELEASE_FLAG_KEY);
  return fromFlagsmith || fromOfflineCache;
};

/**
 * ARC-402: Gates the Tiptap code-block feature (extension, slash entry,
 * text-menu button, and fenced markdown round-trip). Default OFF; flipped
 * per-environment in Flagsmith.
 */
export const useIsScribeBlockEditorCodeblockEnabled = () => {
  return useBooleanFlagValue('release-scribe-block-editor-codeblock', false);
};

/**
 * Non-hook companion for `useIsScribeBlockEditorCodeblockEnabled`. Used by
 * plain-JS call sites: the slash-command group factory and the markdown
 * helpers (`simpleMarkdownToHtml` / `simpleHtmlToMarkdown`), which are not
 * React components.
 */
export const isScribeBlockEditorCodeblockEnabled = () => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'release-scribe-block-editor-codeblock',
    false
  );
};

/**
 * The Session Page V2 kill switch itself, WITH its last online decision
 * replayed while the flag provider is unreachable — the same treatment
 * `useIsNewSessionListEnabled` gets above, and for the same reason.
 *
 * Only the layout gate (here) and the invite ({@link useSessionPageV2InviteVariant})
 * read this replay. The Labs experiment card (`useExperiments`) and the
 * avatar-menu layout toggle stay on the live read, so they can disappear
 * mid-session while the replay still holds the layout up for the same user —
 * an accepted mixed state, not a bug. The invite deliberately remains visible
 * across an online-to-offline transition so the page does not change shape
 * mid-consult, even though its backend-writing actions cannot complete offline.
 * The Labs settings card keeps its live read because its opt-out cannot be
 * honoured offline.
 *
 * Flags composed with this gate remain live-only. Offline, the artifact pane
 * therefore falls back to V1 and the invite falls back to its Labs variant;
 * those safe mixed states are accepted rather than widening this cache beyond
 * the two layout flags involved.
 */
const useIsSessionPageV2FlagEnabled = () => {
  const fromFlagsmith = useBooleanFlagValue(SESSION_PAGE_V2_FLAG_KEY, false);
  const fromOfflineCache = useCachedFlagFallback(SESSION_PAGE_V2_FLAG_KEY);
  return fromFlagsmith || fromOfflineCache;
};

/**
 * Gates the Scribe session page V2 redesign — new IA and general UI improvements.
 *
 * V2 is built assuming the `/scribe/sessions/[id]` route is the entry point
 * (sessions list on the left, breadcrumb context, surrounding panel chrome).
 * It cannot render correctly on the legacy `/scribe/session/[id]` route, so
 * the dependency on `release-scribe-enable-new-session-list` is enforced
 * here: a V2 rollout that gets ahead of the new-list rollout falls back to
 * V1 silently rather than shipping broken UI. When the new-list flag reaches
 * 100% and is removed, drop this dependency too.
 *
 * All scribing logic (recording, transcription, note gen, chat) is reused;
 * only the visual layout forks. The single swap point lives inside `SessionPageContent`.
 *
 * `release-scribe-session-page-v2` gates every V2 surface — the layout (here),
 * the Labs experiment card (`useExperiments`), the avatar-menu layout toggle,
 * and the V1 opt-in journey ({@link useSessionPageV2InviteVariant}) — so
 * flipping it OFF is the ONLINE kill switch: the toggle disappears and
 * opted-in users revert to V1 once Flagsmith answers. Offline, it is not
 * immediate for every surface: the layout gate replays its last ON decision
 * (`useIsSessionPageV2FlagEnabled` above) and holds V2 up until the provider
 * next answers — the Labs card and the avatar-menu toggle stay on the live
 * read and can flip back mid-session even while the replay still holds the
 * layout up for the same user, an accepted mixed state. The follow-up banner
 * has an additional independent rollout switch,
 * `release-scribe-session-page-v2-banner`. Within the main flag's reach, V2 is
 * **opt-in-required**: the layout only turns on once the user explicitly opts
 * in via Heidi Labs (`optIn === true`); an undecided user stays on V1 and sees
 * the invite journey selected by the GA change-management flag.
 */
export const useIsSessionPageV2Enabled = () => {
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const flagValue = useIsSessionPageV2FlagEnabled();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  const isManagedReleaseEnabled = useIsSessionLayoutManagedReleaseEnabled();
  // Only the managed-release cohort can override auto-enrolment explicitly.
  // Undecided users follow the stamp; release gates still control availability.
  const showV2 = isManagedReleaseEnabled
    ? (optIn ?? isAutoEnrolled)
    : isAutoEnrolled || optIn === true;
  return isNewSessionListEnabled && (flagValue || isManagedReleaseEnabled) && showV2;
};

export type SessionPageV2InviteVariant = 'labs' | 'ga';

// Managed release or GA selects Delay/X: seen-state changes, opt-in stays undecided.
// Otherwise Labs dismissal opts out. Enrolled or decided users need no invite.
export const useSessionPageV2InviteVariant = (): SessionPageV2InviteVariant | null => {
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const optIn = useLabsFeatureOptIn(LABS_FEATURE.sessionLayoutV2);
  const flagValue = useIsSessionPageV2FlagEnabled();
  const isGaChangeManagementEnabled = useIsSessionPageV2GaChangeManagementEnabled();
  const isAutoEnrolled = useIsAutoEnrolledSessionLayoutV2();
  const isManagedReleaseEnabled = useIsSessionLayoutManagedReleaseEnabled();
  // The managed-release cohort is invited while the list is still off for it:
  // accepting the invite is what turns the list on.
  const isEligible =
    optIn === undefined &&
    !isAutoEnrolled &&
    ((isNewSessionListEnabled && flagValue) || isManagedReleaseEnabled);

  if (!isEligible) return null;
  return isManagedReleaseEnabled || isGaChangeManagementEnabled ? 'ga' : 'labs';
};

/*
 * Gates the new "For action" session review status across the apply and filter
 * surfaces (session header, V2 status row, session-list inline picker, Action
 * Center, and the status filters). Rollout / kill gate — not an experiment, so
 * no `useManualExposure`.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so the rollout can be ramped and
 * targeted from PostHog. The backend has no gate on this status — it accepts and
 * returns `FOR_ACTION` unconditionally — so this purely controls FE selection.
 *
 * Display is intentionally NOT gated — `sessionStatusChipConfig` and
 * `enumToDescription` always resolve `FOR_ACTION`, so a session already set to
 * "For action" still renders its chip/label/colour when the flag is off; only
 * the ability to *select* or *filter by* it is gated.
 */
export const useIsForActionSessionStatusEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-enable-for-action-status') ?? false;
};

/**
 * Gates every session "urgency" surface behind one flag: the session-header
 * chip (set), the session-list urgency filter (desktop + mobile + side-nav) and
 * its "Order by" sort option, AND the display surfaces — the row marker
 * (`SessionUrgencyFlag`, which self-gates) and the urgent row/card tint. With
 * the flag off, urgency is invisible and inert everywhere — nothing to set,
 * filter, sort, or display.
 *
 * PostHog-backed (`useFeatureFlagEnabled`) so it can be ramped/targeted from
 * PostHog and stays in lock-step with the backend (see
 * `docs/contracts/session-urgency.md`) — mirroring the For-action status flag.
 * Rollout/kill gate, not an experiment, so no `useManualExposure`.
 */
export const useIsSessionUrgencyEnabled = () => {
  return useFeatureFlagEnabled('release-scribe-enable-session-urgency') ?? false;
};

export const IS_CUSTOM_EXPORT_HEADER_FOOTER_ENABLED = () => {
  return posthog.isFeatureEnabled('release-custom-export-header-footer');
};

export const IS_LIVE_TRANSCRIPTS_ENABLED = () => {
  // On-prem serves the live-transcript endpoint from ml-scribe itself
  // (uploaded audio → whisper → GET /sessions/:id/transcript/live, which
  // returns the user_uploaded transcript). Use the live pane rather than the
  // legacy fetch-only path so on-prem gets the richer transcript UI.
  if (isOnPrem) {
    return true;
  }
  if (process.env.NEXT_PUBLIC_IS_TAURI) {
    return false;
  }
  if (USE_RECORDING_V2()) {
    return true;
  }
  const isGeminiTranscriber =
    posthog.getFeatureFlag('config-default-transcriber') === 'Gemini';
  return (
    (isGeminiTranscriber || IS_CHUNK_RECORDING_ENABLED()) &&
    posthog.isFeatureEnabled('release-live-transcripts')
  );
};

export const IS_BLOCK_EDITOR_V1_ENABLED = () => {
  return posthog.isFeatureEnabled('release-block-editor-v1') ?? false;
};

export const IS_TAB_RENAME_ENABLED = () => {
  return posthog.isFeatureEnabled('release-tab-rename');
};

export const IS_LESS_DEBOUNCED_NOTE_DOCUMENT_UPDATE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-less-debounced-note-document-update');
};

export const IS_ENTITY_VERIFICATION_ENABLED = () => {
  return posthog.isFeatureEnabled('release-entity-verification') ?? false;
};

/** PostHog multivariate — auto-mode is enabled when the variant starts with AUTO_. */
export const IS_HEIDI_VERIFY_AUTO_MODE_ENABLED = () => {
  const variant = posthog.getFeatureFlag('release-entity-verification');
  return typeof variant === 'string' && variant.startsWith('AUTO_');
};

/**
 * GRO-3266: side-by-side note preference experiment. Runs through the existing
 * PostHog `config-alternative-generation-v2` flag (the single flag that drives
 * every backend alternative-generation experiment) instead of a dedicated flag,
 * so the whole side-by-side workflow stays in one place.
 *
 * The `SBS_PREF` variant is the treatment arm: ml-scribe returns a second
 * `model_comparison` note (no upsell) and the FE shows the side-by-side
 * comparison for both template and Auto-mode notes. Every other value — incl.
 * the PostHog `control` holdout and unenrolled users — keeps the single-note
 * flow. Reading the flag through `@heidi/session/flags` also emits PostHog's native `$feature_flag_called`
 * exposure, so no manual Amplitude exposure is required.
 */
export const SIDE_BY_SIDE_NOTE_PREFERENCE_VARIANT_KEY = 'SBS_PREF';

export const IS_BBE_CONTEXT_TAB_ENABLED = () => {
  return posthog.isFeatureEnabled('release-bbe-context-tab');
};

export const IS_ENABLE_FULL_RICH_TEXT_ENABLED = () => {
  return posthog.isFeatureEnabled('enable-full-rich-text');
};

export const IS_OPTIMITCALLY_CREATE_EMPTY_DOCUMENT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-optimistically-create-empty-document');
};

export const IS_STEP_LOADING_ENABLED = () => {
  return posthog.isFeatureEnabled('release-step-loading-progression');
};

export const IS_SIDE_BY_SIDE_NOTE_GEN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-side-by-side-note-gen') ?? false;
};

export const IS_SIDE_BY_SIDE_TIMINGS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-side-by-side-note-gen-timings') ?? false;
};

export const IS_UPCOMING_SESSIONS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-upload-upcoming-sessions');
};

export const IS_CONSOLIDATED_SESSIONS_LIST_ENABLED = () => {
  return posthog.isFeatureEnabled('release-consolidated-sessions-list');
};

export const IS_WEB_TAB_SHARING_ENABLED = () => {
  // Web-only: desktop uses native capture, not browser tab sharing.
  if (isTauri()) return false;
  return true;
};

export const IS_INTERACTIVE_SESSION_START_ENABLED = () => {
  return posthog.isFeatureEnabled('release-interactive-session-start');
};

/** Multivariate shipped 100% → `skip-selection-bottom` (PostHog `side-by-side-skip-selection-button`). */
export const SIDE_BY_SIDE_SKIP_SELECTION_BUTTON_CONFIG = ():
  | 'skip-selection-top'
  | 'skip-selection-bottom'
  | 'disabled' => {
  return 'skip-selection-bottom';
};

/**
 * Transcript Chips: Real-time Evidence query suggestions during recording.
 * Shows suggested queries based on live transcript content.
 * Kill switch for AI clinical suggestion / red-flag chips — does not gate shortcuts.
 */
export const IS_TRANSCRIPT_CHIPS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-transcript-chips') ?? false;
};

/**
 * Linked Sessions Auto-Select: When enabled, auto-selects recent sessions from the
 * same patient profile as fallback linked sessions for context.
 * When disabled, users must manually select linked sessions.
 */
export const IS_LINKED_SESSIONS_AUTO_SELECT_ENABLED = () => {
  return posthog.isFeatureEnabled('release-linked-sessions-auto-select') ?? false;
};

export const IS_CONSULT_NOTE_EDIT_DISPLAY_ENABLED = () => {
  return posthog.isFeatureEnabled('release-consult-note-edit-display');
};

export const IS_EXPORT_QR_CODE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-export-qr-code');
};

/**
 * Kill switch for the clipboard write recovery: the synchronous native write on
 * Safari/WebKit and, on the polyfill path, the direct native attempt (with its
 * bounded hold for an unfocused window) that runs once the retries are spent.
 * Flip ON to skip both without a revert — which is the only way to reach a
 * desktop bundle that has already shipped. It does NOT disable the Tauri
 * OS-clipboard rescue: that still runs after the retries on desktop, so a
 * killed recovery is the polyfill plus that rescue, not the polyfill alone.
 * Non-hook so the pure clipboard helper can read it imperatively.
 */
export const isClipboardWriteRecoveryKilled = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    'kill-scribe-copy-clipboard-recovery',
    false
  );
